import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    FolderLock,
    Network,
    Compass,
    Coins,
    Users,
    Settings,
    FileWarning,
    LogOut,
    Menu,
    X,
} from "lucide-react";
import { useCockpitAuth } from "@/lib/cockpitAuth";

const NAV = [
    { to: "/cockpit", label: "Vue d'ensemble", icon: LayoutDashboard, end: true },
    { to: "/cockpit/dossiers", label: "Dossiers", icon: FolderLock },
    { to: "/cockpit/finance", label: "Finance", icon: Coins },
    { to: "/cockpit/administratif", label: "Administratif", icon: FileWarning },
    { to: "/cockpit/partenaires", label: "Réseau XV", icon: Network },
    { to: "/cockpit/prospection", label: "Prospection", icon: Compass },
    { to: "/cockpit/utilisateurs", label: "Comptes", icon: Users, superOnly: true },
    { to: "/cockpit/parametres", label: "Sécurité", icon: Settings },
];

function SidebarBody({ user, isSuper, onItemClick, onLogout }) {
    return (
        <>
            <div className="px-7 py-8 border-b border-white/[0.06]">
                <div className="flex items-center gap-3">
                    <img
                        src="/brand/icon-gold.png"
                        alt=""
                        className="h-7 w-auto opacity-90"
                    />
                    <div className="flex flex-col leading-none">
                        <span
                            className="font-serif text-[14px] tracking-[0.32em] uppercase text-[#f4ecd8]"
                            style={{ fontWeight: 400 }}
                        >
                            XV Cockpit
                        </span>
                        <span className="mt-1.5 text-[8.5px] tracking-[0.4em] uppercase text-[#d4af37]/80">
                            Strategic Ops · v1
                        </span>
                    </div>
                </div>
            </div>

            <nav className="flex-1 px-4 py-6 overflow-y-auto">
                <div className="overline text-[#d4af37]/70 px-3 mb-4 text-[10px] tracking-[0.32em]">
                    Cockpit
                </div>
                <ul className="space-y-1">
                    {NAV.filter((n) => !n.superOnly || isSuper).map((item) => {
                        const Icon = item.icon;
                        return (
                            <li key={item.to}>
                                <NavLink
                                    to={item.to}
                                    end={item.end}
                                    onClick={onItemClick}
                                    data-testid={`cockpit-nav-${item.to.split("/").pop()}`}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-3 py-2.5 text-[13px] tracking-[0.06em] transition-colors duration-500 rounded-sm ${
                                            isActive
                                                ? "text-[#d4af37] bg-white/[0.04]"
                                                : "text-[#f4ecd8]/70 hover:text-[#f4ecd8] hover:bg-white/[0.03]"
                                        }`
                                    }
                                >
                                    <Icon strokeWidth={1.2} size={16} />
                                    <span>{item.label}</span>
                                </NavLink>
                            </li>
                        );
                    })}
                </ul>
            </nav>

            <div className="px-5 py-6 border-t border-white/[0.06]">
                <div className="px-2 mb-4">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70">
                        Session
                    </div>
                    <div className="mt-2 font-serif text-[15px] text-[#f4ecd8] leading-tight">
                        {user.name || user.email}
                    </div>
                    <div className="text-[11px] text-[#f4ecd8]/50 mt-1">
                        {user.role === "super_admin"
                            ? "Super-administrateur"
                            : "Administrateur"}
                    </div>
                </div>
                <button
                    type="button"
                    data-testid="cockpit-logout"
                    onClick={onLogout}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-[11px] tracking-[0.24em] uppercase text-[#f4ecd8]/60 hover:text-[#f4ecd8] hover:bg-white/[0.03] transition-colors duration-500 rounded-sm"
                >
                    Se déconnecter
                    <LogOut strokeWidth={1.2} size={14} />
                </button>
            </div>
        </>
    );
}

export default function CockpitLayout() {
    const { user, logout } = useCockpitAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Auto-close drawer on route change
    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    const handleLogout = () => {
        logout();
        navigate("/cockpit/login");
    };

    if (!user) return null;
    const isSuper = user.role === "super_admin";

    return (
        <div className="min-h-screen bg-[#0b1220] text-[#f4ecd8] font-sans-modern">
            {/* Subtle gold radial accent */}
            <div
                aria-hidden="true"
                className="pointer-events-none fixed inset-0 opacity-[0.04]"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 8% 8%, rgba(212,175,55,0.55) 0px, transparent 36%), radial-gradient(circle at 92% 88%, rgba(212,175,55,0.4) 0px, transparent 42%)",
                }}
            />

            {/* MOBILE topbar (visible <lg) */}
            <header className="lg:hidden sticky top-0 z-30 bg-[#070d18]/95 backdrop-blur-md border-b border-white/[0.06]">
                <div className="flex items-center justify-between px-5 h-16">
                    <div className="flex items-center gap-3">
                        <img
                            src="/brand/icon-gold.png"
                            alt=""
                            className="h-6 w-auto opacity-90"
                        />
                        <span
                            className="font-serif text-[12px] tracking-[0.32em] uppercase text-[#f4ecd8]"
                            style={{ fontWeight: 400 }}
                        >
                            XV Cockpit
                        </span>
                    </div>
                    <button
                        type="button"
                        data-testid="cockpit-menu-toggle"
                        onClick={() => setDrawerOpen(true)}
                        className="p-2 -mr-2 text-[#d4af37]/90 hover:text-[#d4af37] transition-colors"
                        aria-label="Ouvrir le menu"
                    >
                        <Menu strokeWidth={1.3} size={22} />
                    </button>
                </div>
            </header>

            <div className="relative flex min-h-screen">
                {/* DESKTOP SIDEBAR (visible ≥lg) */}
                <aside
                    data-testid="cockpit-sidebar"
                    className="hidden lg:flex w-[260px] shrink-0 bg-[#070d18] border-r border-white/[0.06] flex-col"
                >
                    <SidebarBody
                        user={user}
                        isSuper={isSuper}
                        onLogout={handleLogout}
                    />
                </aside>

                {/* MOBILE DRAWER (visible <lg, when open) */}
                {drawerOpen && (
                    <div className="lg:hidden fixed inset-0 z-50 flex">
                        {/* Backdrop */}
                        <button
                            type="button"
                            aria-label="Fermer le menu"
                            onClick={() => setDrawerOpen(false)}
                            className="absolute inset-0 bg-[#070d18]/80 backdrop-blur-sm"
                        />
                        <aside
                            data-testid="cockpit-drawer"
                            className="relative w-[280px] max-w-[85vw] bg-[#070d18] border-r border-white/[0.08] flex flex-col animate-[slideInLeft_0.32s_ease-out]"
                        >
                            <button
                                type="button"
                                data-testid="cockpit-drawer-close"
                                onClick={() => setDrawerOpen(false)}
                                className="absolute top-5 right-5 z-10 p-2 text-[#f4ecd8]/60 hover:text-[#f4ecd8] transition-colors"
                                aria-label="Fermer"
                            >
                                <X strokeWidth={1.3} size={20} />
                            </button>
                            <SidebarBody
                                user={user}
                                isSuper={isSuper}
                                onItemClick={() => setDrawerOpen(false)}
                                onLogout={handleLogout}
                            />
                        </aside>
                    </div>
                )}

                {/* MAIN */}
                <main className="flex-1 min-w-0">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
